import React, { useState } from 'react';
import { 
  Plus, 
  Eye, 
  Star, 
  Clock, 
  Tag,
  Calculator,
  FileText,
  BookOpen,
  Lightbulb
} from 'lucide-react';
import { clsx } from 'clsx';
import MathRenderer from '../math/MathRenderer';
import SolutionPreview from './SolutionPreview';

const SolutionCard = ({ solution, onInsert }) => {
  const [showPreview, setShowPreview] = useState(false);

  const getTypeIcon = (type) => {
    switch (type) {
      case 'solution': return Calculator;
      case 'example': return FileText;
      case 'theorem': return BookOpen;
      case 'formula': return Lightbulb;
      default: return FileText;
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'easy': return 'text-green-600 bg-green-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'hard': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const TypeIcon = getTypeIcon(solution.type);

  return (
    <>
      <div className="group bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center space-x-2">
            <TypeIcon className="w-4 h-4 text-sigma-600" />
            <span className="text-xs font-medium text-sigma-600 uppercase tracking-wide">
              {solution.type}
            </span>
          </div>
          
          <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowPreview(true);
              }}
              className="p-1 text-gray-400 hover:text-gray-600 rounded"
              title="Preview"
            >
              <Eye className="w-4 h-4" />
            </button>
            
            <button
              onClick={(e) => {
                e.stopPropagation();
                onInsert();
              }}
              className="p-1 text-sigma-600 hover:text-sigma-700 rounded"
              title="Insert into document"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Title */}
        <h3 className="font-medium text-gray-900 mb-2 line-clamp-2">
          {solution.title}
        </h3>

        {/* Description */}
        {solution.description && (
          <p className="text-sm text-gray-600 mb-3 line-clamp-2">
            {solution.description}
          </p>
        )}

        {/* Content Preview */}
        {solution.mathContent ? (
          <div className="mb-3 p-2 bg-gray-50 rounded text-center">
            <MathRenderer latex={solution.mathContent} inline />
          </div>
        ) : solution.content && (
          <div className="mb-3 p-2 bg-gray-50 rounded text-sm text-gray-700 line-clamp-3">
            {solution.content}
          </div>
        )}

        {/* Tags */}
        <div className="flex flex-wrap gap-1 mb-3">
          {solution.subject && (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              {solution.subject}
            </span>
          )}
          
          {solution.gradeLevel && (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
              {solution.gradeLevel.replace('_', ' ')}
            </span>
          )}
          
          {solution.difficulty && (
            <span className={clsx(
              'inline-flex items-center px-2 py-1 rounded-full text-xs font-medium capitalize',
              getDifficultyColor(solution.difficulty)
            )}>
              {solution.difficulty}
            </span>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between text-xs text-gray-500">
          <div className="flex items-center space-x-3">
            {solution.topic && (
              <div className="flex items-center">
                <Tag className="w-3 h-3 mr-1" />
                <span className="capitalize">{solution.topic}</span>
              </div>
            )}
            
            <div className="flex items-center">
              <Clock className="w-3 h-3 mr-1" />
              <span>{solution.estimatedTime || '5'} min</span>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            {solution.rating && (
              <div className="flex items-center">
                <Star className="w-3 h-3 text-yellow-500 mr-1" />
                <span>{solution.rating.toFixed(1)}</span>
              </div>
            )}
            
            <span>{solution.views || 0} views</span>
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <SolutionPreview
          solution={solution}
          onClose={() => setShowPreview(false)}
          onInsert={() => {
            onInsert();
            setShowPreview(false);
          }}
        />
      )}
    </>
  );
};

export default SolutionCard;